import type { ReactNode } from 'react';
import { NOT_NEEDED_NOTE, WIZARD_STEP_DEFS } from '../wizardSteps';
import { LiveRegion, StepHeading } from './basics';

interface Props {
  /** 0-based index into WIZARD_STEP_DEFS. */
  currentIndex: number;
  announcement: string;
  /** Steps that do not apply to this import (column matching for the native EPN formats). They are shown, marked
   * "not needed", and skipped. */
  notNeeded?: number[];
  children: ReactNode;
}

/** Stepper (nav + ordered list with aria-current="step"), the step heading that receives focus on every
 * step change, and the polite live region. Remounted per step via `key` by the caller. */
export function WizardFrame({ currentIndex, announcement, notNeeded = [], children }: Props) {
  const title = WIZARD_STEP_DEFS[currentIndex]?.title ?? 'Import Payroll Data';
  return (
    <div>
      <nav aria-label="Import progress">
        <ol className="phi-stepper">
          {WIZARD_STEP_DEFS.map(({ label }, index) => {
            const skipped = notNeeded.includes(index);
            const done = index < currentIndex && !skipped;
            const classes = [done ? 'phi-step--done' : '', skipped ? 'phi-step--skipped' : ''].filter(Boolean).join(' ');
            return (
              <li key={label} className={classes || undefined} aria-current={index === currentIndex ? 'step' : undefined}>
                <span className="phi-step-index" aria-hidden="true">{done ? '✓' : skipped ? '–' : index + 1}</span>
                <span>
                  {label}
                  {done && <span className="phi-sr-only"> (completed)</span>}
                  {skipped && <span className="phi-step-note"> (not needed)</span>}
                  {skipped && <span className="phi-sr-only">: {NOT_NEEDED_NOTE}</span>}
                </span>
              </li>
            );
          })}
        </ol>
      </nav>
      <StepHeading>{title}</StepHeading>
      <LiveRegion message={announcement} />
      {children}
    </div>
  );
}
